/**
 * The folder-rule pattern matcher (#616).
 *
 * Pure. Compiles a tenant's folder rules into something the write path can ask
 * "how many versions does a file at this path keep?" once per file write.
 *
 * ── WHAT A PATTERN MATCHES: ANY PATH SEGMENT, FILE OR DIRECTORY ───────────
 *
 * A pattern is matched against the file's whole tree-relative path. It may
 * name a directory — in which case it covers everything beneath it — or it may
 * name the file itself:
 *
 *   node_modules        every file under any `node_modules` directory
 *   docs/*.txt          the `.txt` files directly inside a rooted `docs`
 *   *.log               a log file anywhere, and anything under a directory
 *                       whose name ends `.log`
 *
 * It matched DIRECTORIES ONLY until #616's pattern pass: the last segment of a
 * path was dropped before matching, so `docs/*.txt` compiled fine, saved fine,
 * displayed fine and matched nothing, for the same reason `*.log` did. A rule
 * that silently matches nothing is indistinguishable from one that has not
 * taken effect yet, which is the failure mode this surface exists to avoid.
 *
 * The per-extension axis (one layer up the #590 ladder) still exists and is
 * still the right tool for "every `.psd` in the drive". A pattern is for
 * "`.psd` under THIS path", which that axis cannot say.
 *
 * ── SYNTAX ────────────────────────────────────────────────────────────────
 *
 *   node_modules      a file or directory with this NAME, at any depth
 *   *.cache           a name glob, at any depth
 *   build/**          the rooted `build` and everything below it
 *   a/b               `a/b`, and everything below it
 *   src/** /*.test.ts  a rooted `src`, then ANY number of directories, then a
 *                     name ending `.test.ts`
 *   ** /foo           equivalent to `foo` (leading `** /` is a no-op)
 *
 * A pattern with no `/` is a NAME rule and floats to any depth — it compiles
 * to a leading `**`, so there is one matching routine rather than two. A
 * pattern with a `/` is ROOTED at the tree root. That split is gitignore's,
 * and it is the one users already hold: `node_modules` everywhere, `build/`
 * only mine.
 *
 * `**` matches ZERO OR MORE segments; a `*` matches within ONE. Before the
 * pattern pass `**` was handled by the single-segment matcher, where it
 * matched exactly one segment — so `src/** /*.ts` missed `src/a.ts` (nothing
 * for `**` to eat) and `src/a/b/c.ts` (too many), and hit only the one depth
 * in between. That is a wrong answer rather than a missing feature, which is
 * why it is implemented rather than refused.
 *
 * A rule covers everything below what it names. There is no negation
 * (`!pattern`); a subtree that needs a different number gets its own rule, and
 * the FIRST rule in list order wins — see `ExclusionMatcher.retentionFor`.
 *
 * ── WHY THIS IS HAND-WRITTEN AND NOT A RegExp ─────────────────────────────
 *
 * Patterns are user input and this runs on the write path. Compiling user text
 * into a `RegExp` puts catastrophic backtracking one paste away from a
 * tenant's own sync, and the damage would look like a hang rather than an
 * error. Segment-wise matching has no backtracking beyond the single `*`
 * wildcard it implements, which is bounded by segment length.
 *
 * The other reason is correctness of intent: users write `.git` meaning a
 * literal dot. Under regex that also matches `agit` and their `logit/` folder,
 * silently.
 */

import {
  MAX_VERSION_RETENTION,
  isValidFolderRetention,
  type FolderRetention,
} from './version-retention-bounds.js'

/**
 * One folder rule: a pattern, and how many versions the files under it keep.
 *
 * ── THIS IS A WRITE DECISION, AND ONLY A WRITE DECISION ───────────────────
 *
 * `versions` is read in `cleanupOldVersions`, once per file write, AFTER the
 * file has been walked, stat'd, read, hashed, row-written and CAS-written. It
 * prunes older version rows once the new one exists. Nothing here is consulted
 * by the walk, and no value of it makes a directory cheaper to traverse.
 *
 * That is worth stating plainly because the rule USED to carry a second,
 * walk-time meaning (`null`, "never descend"), and the performance argument for
 * it was real: `node_modules` is ~110,000 rows, and `.git` re-hashes a 1.4 MB
 * index on every `git status`. It was removed anyway — see `FolderRetention`
 * for the three surfaces that disagreed about what existed while it was there.
 *
 * So a rule of `1` on `node_modules` bounds the HISTORY, not the traversal.
 * Anyone reintroducing a skip state has to answer the storage, listing and
 * agent-visibility questions that removed it.
 */
export interface ExclusionRule {
  pattern: string
  /** `1..MAX_VERSION_RETENTION` — how many versions files under this pattern
   *  keep. A file that exists is version 1, so there is no `0` on this
   *  scale, and there is no "off" — see `FolderRetention`. */
  versions: FolderRetention
}

/** Longest a single pattern may be. Generous for a path, far below anything
 *  that would make segment matching expensive. */
export const MAX_PATTERN_LENGTH = 200
/** Most patterns one tenant may store. Each is checked per directory entry, so
 *  this is the per-entry work budget, not a storage limit. */
export const MAX_PATTERNS = 100

/** Why a pattern was refused. The route turns this into a readable 400. */
export type PatternRejection =
  | 'empty'
  | 'too-long'
  | 'absolute'
  | 'traversal'
  | 'only-wildcards'
  | 'bad-versions'
  | 'duplicate'

export interface PatternProblem {
  pattern: string
  reason: PatternRejection
}

/**
 * Normalise one pattern, or say why it cannot be used.
 *
 * Rejections are refusals, never silent repairs: a pattern the user cannot see
 * the effect of is the failure mode this whole feature has to avoid.
 */
export function normalizePattern(raw: string): { ok: true; value: string } | { ok: false; reason: PatternRejection } {
  if (typeof raw !== 'string') return { ok: false, reason: 'empty' }
  let p = raw.trim()
  if (p.length === 0) return { ok: false, reason: 'empty' }
  if (p.length > MAX_PATTERN_LENGTH) return { ok: false, reason: 'too-long' }
  // A leading slash reads as "from the tree root", which is what a rooted
  // pattern already means — accept the spelling rather than refusing it.
  if (p.startsWith('/')) p = p.slice(1)
  // Trailing slash is how people write "a directory" (`build/`). Everything
  // here is a directory, so it carries no extra meaning; drop it.
  while (p.endsWith('/')) p = p.slice(0, -1)
  // Collapse repeated separators so `a//b` and `a/b` are one pattern.
  p = p.replace(/\/{2,}/g, '/')
  // `**/foo` is `foo`: a leading "any depth" prefix is exactly what a bare
  // name already does, and keeping both spellings would mean two code paths
  // reaching the same answer.
  while (p.startsWith('**/')) p = p.slice(3)
  // A trailing `/**` means "and everything below", which excluding a directory
  // already implies. Strip it so `build` and `build/**` compile identically.
  while (p.endsWith('/**')) p = p.slice(0, -3)
  if (p.length === 0) return { ok: false, reason: 'empty' }
  if (/^[a-zA-Z]:/.test(p)) return { ok: false, reason: 'absolute' }
  // `..` would let a pattern reach outside the tree it is scoped to. It cannot
  // actually escape — matching is done on tree-relative paths — but a pattern
  // nobody can reason about is worse than a refused one.
  if (p.split('/').some(seg => seg === '..' || seg === '.')) return { ok: false, reason: 'traversal' }
  // `*`, `**`, `*/*` and friends match every path in the tree. Almost
  // certainly a mistake, and the one that would quietly put the whole drive on
  // one version count — refuse it rather than let someone discover it later.
  if (p.split('/').every(seg => /^\*+$/.test(seg))) return { ok: false, reason: 'only-wildcards' }
  return { ok: true, value: p }
}

/**
 * A one-line, human-readable reason. Kept beside the union so the API and the
 * settings screen say the same thing about the same refusal.
 *
 * The API returns the machine-readable `reason` ALONGSIDE this, and the screen
 * renders its own translated copy keyed off `reason` — the English here is the
 * fallback and what a direct API caller reads, never something the UI has to
 * parse.
 */
export function describePatternRejection(reason: PatternRejection): string {
  switch (reason) {
    case 'empty': return 'is blank'
    case 'too-long': return `is longer than ${MAX_PATTERN_LENGTH} characters`
    case 'absolute': return 'looks like an absolute path; patterns are relative to the drive root'
    case 'traversal': return 'contains "." or ".."'
    case 'only-wildcards': return 'would exclude every folder'
    case 'bad-versions': return `needs a whole number from 1 to ${MAX_VERSION_RETENTION}, or null for "not synced"`
    case 'duplicate': return 'is listed twice with two different version counts'
  }
}

/** A whole list was refused. `problems` is empty for `too-many` — the fault is
 *  the list's, not any one pattern's. */
export interface PatternListRejection {
  ok: false
  reason: 'not-a-list' | 'not-a-string' | 'too-many' | 'invalid-pattern'
  problems: PatternProblem[]
  /** One sentence, ready to put in a 400 body. */
  message: string
}

/**
 * Validate a candidate pattern list on the WRITE path.
 *
 * Unlike `compileExclusions` — which is deliberately total, because the read
 * path must never let one bad stored pattern take a sync down — this REFUSES,
 * and names every pattern it refused. A save is the one moment the user is
 * looking, so it is the only moment at which telling them costs nothing.
 *
 * On success the returned values are NORMALISED and de-duplicated, and it is
 * those that get stored: `build/`, `/build` and `build/**` are one rule, and
 * storing the user's spelling would mean the screen shows three rules where
 * the engine has one.
 */
export function validateExclusionRules(
  input: unknown,
): { ok: true; value: ExclusionRule[] } | PatternListRejection {
  const SHAPE = 'rules must be an array of { pattern, versions }, or null to clear'
  if (!Array.isArray(input)) {
    return { ok: false, reason: 'not-a-list', problems: [], message: SHAPE }
  }
  if (input.length > MAX_PATTERNS) {
    return { ok: false, reason: 'too-many', problems: [], message: `at most ${MAX_PATTERNS} rules` }
  }
  const problems: PatternProblem[] = []
  /** normalised pattern → the value already accepted for it. */
  const seen = new Map<string, FolderRetention>()
  const value: ExclusionRule[] = []
  for (const raw of input) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return { ok: false, reason: 'not-a-string', problems: [], message: SHAPE }
    }
    const { pattern, versions } = raw as { pattern?: unknown; versions?: unknown }
    if (typeof pattern !== 'string') {
      return { ok: false, reason: 'not-a-string', problems: [], message: SHAPE }
    }
    const n = normalizePattern(pattern)
    if (!n.ok) { problems.push({ pattern, reason: n.reason }); continue }
    // Checked AFTER the pattern so a row with both faults reports the one the
    // user is likelier to have meant, and only ever reports one per row.
    if (!isValidFolderRetention(versions)) {
      problems.push({ pattern, reason: 'bad-versions' }); continue
    }
    // `build/`, `/build` and `build/**` normalise to one rule. Two spellings
    // of the same rule carrying DIFFERENT values is refused rather than
    // resolved: whichever one we picked, the screen would show a number the
    // engine does not use, which is the exact failure this feature's preview
    // exists to make impossible.
    if (seen.has(n.value)) {
      if (seen.get(n.value) === versions) continue
      problems.push({ pattern, reason: 'duplicate' }); continue
    }
    seen.set(n.value, versions)
    value.push({ pattern: n.value, versions })
  }
  if (problems.length > 0) {
    return {
      ok: false,
      reason: 'invalid-pattern',
      problems,
      message: problems.map(p => `"${p.pattern}" ${describePatternRejection(p.reason)}`).join('; '),
    }
  }
  return { ok: true, value }
}

/** Does `name` match one glob SEGMENT (`*` matches within the segment)? */
function segmentMatches(pattern: string, name: string): boolean {
  if (pattern === '*') return name.length > 0
  if (!pattern.includes('*')) return pattern === name
  // Split on `*` and consume the literal parts in order. Linear in the
  // segment: each literal is found once, left to right, and never retried —
  // which is the property a RegExp would not give us for free.
  const parts = pattern.split('*')
  const first = parts[0]!
  const last = parts[parts.length - 1]!
  if (!name.startsWith(first)) return false
  if (!name.endsWith(last)) return false
  // The two anchors must fit side by side without sharing a character. `*` is
  // zero-or-more, so `a*b` DOES match `ab` (1 + 1 <= 2) — what this rejects is
  // `a*a` against `a`, where the same single character would have to serve as
  // both the prefix and the suffix.
  if (first.length + last.length > name.length) return false
  let at = first.length
  for (let i = 1; i < parts.length - 1; i++) {
    const mid = parts[i]!
    if (mid === '') continue
    const found = name.indexOf(mid, at)
    if (found === -1 || found + mid.length > name.length - last.length) return false
    at = found + mid.length
  }
  return true
}


/** One compiled pattern. */
interface CompiledPattern {
  /**
   * The segments to match a path against. A bare NAME rule carries a synthetic
   * leading `**` so it floats to any depth — which makes `foo` and `**​/foo`
   * literally the same compiled thing, and leaves ONE matching routine instead
   * of a rooted branch and a name branch that have to agree with each other.
   */
  segs: string[]
  /**
   * The segments the pattern itself NAMES, without the synthetic `**`. This is
   * what `shadowedRules` asks about — "does an earlier rule cover the thing
   * this one names" — and it must not include the prefix we added, or every
   * name rule would be asking about a path starting with a literal `**`.
   */
  named: string[]
  /** The normalised pattern as written — what the screen names. */
  pattern: string
  /** How many versions files under this pattern keep. */
  versions: FolderRetention
}

/** The segment that matches zero or more path segments. */
const DEEP = '**'

/**
 * Does this rule cover `segs` — a path's FULL segment list, file name
 * included?
 *
 * Two things are going on, and they are both deliberate:
 *
 *   PREFIX. The pattern need only match a prefix. That is what makes a rule
 *   cover a subtree: `build` matches `build/a/b.js` by consuming `build` and
 *   accepting whatever is below. It is also why a rule that names a FILE works
 *   without a special case — the prefix simply happens to be the whole path.
 *
 *   `**`. Matches zero or more segments, so `src/**​/*.test.ts` reaches
 *   `src/a.test.ts`, `src/x/a.test.ts` and `src/x/y/a.test.ts` alike.
 *
 * The `**` handling is the standard single-backtrack wildcard walk: remember
 * where the last `**` was, and on a mismatch let it eat one more segment and
 * resume. It backtracks at most once per `**` per segment, so it is O(pattern
 * × path) worst case with no exponential blowup — the property that made
 * hand-written matching the choice over a compiled `RegExp` in the first
 * place, and it must survive any change to this function.
 *
 * Shared by `retentionFor` and `shadowedRules` deliberately. The shadow
 * warning's whole claim is "this rule can never win", and a second opinion
 * about what a rule covers is how a screen ends up warning about a rule that
 * fires, or staying quiet about one that cannot.
 */
function coversPath(pattern: string[], segs: string[]): boolean {
  let p = 0
  let s = 0
  // Where to resume from if a `**` needs to eat another segment. -1 = no `**`
  // seen yet, so a mismatch is final.
  let starP = -1
  let starS = -1
  while (s < segs.length) {
    // The pattern ran out with path left over: it matched a PREFIX, and a rule
    // covers everything below what it names.
    if (p >= pattern.length) return true
    if (pattern[p] === DEEP) { starP = p++; starS = s; continue }
    if (segmentMatches(pattern[p]!, segs[s]!)) { p++; s++; continue }
    if (starP === -1) return false
    // Give the last `**` one more segment and retry from just after it.
    p = starP + 1
    s = ++starS
  }
  // The path ran out. Any `**` still left matches zero segments; anything else
  // is a pattern deeper than the path, which this path cannot satisfy.
  while (p < pattern.length && pattern[p] === DEEP) p++
  return p >= pattern.length
}

/**
 * Compile ONE rule's pattern, or `null` if it is not usable. The single-rule
 * half of `compileExclusions`, pulled out so `shadowedRules` can compile a
 * pattern without building a whole matcher per rule — the obvious spelling
 * there is O(rules²) matchers, and `MAX_PATTERNS` is 100.
 */
function compileOne(pattern: string, versions: FolderRetention): CompiledPattern | null {
  const n = normalizePattern(pattern)
  if (!n.ok) return null
  const named = n.value.split('/')
  // No `/` means a NAME rule: it floats to any depth, which is exactly a
  // leading `**`.
  return { segs: named.length === 1 ? [DEEP, ...named] : named, named, pattern: n.value, versions }
}

/**
 * Rules that can never fire, because a rule ABOVE them already covers
 * everything they cover.
 *
 * The first match in list order wins, so `Videos` at the top and
 * `Videos/Kids` below it means Kids files take the `Videos` number and the
 * second rule is dead. That is legal and it is the ordering the user chose —
 * but from their side it is indistinguishable from a rule that has not taken
 * effect yet, which is the one failure this whole surface has to make
 * impossible. The fix is to drag the specific rule above the general one, so
 * the screen says which row is inert rather than refusing the save.
 *
 * The test is "does the earlier rule cover what this one NAMES" — the same
 * `coversPath` the resolution uses, so the warning cannot disagree with the
 * engine about what is covered. It is deliberately not a
 * proof of full subsumption over every possible path: two overlapping globs
 * (`*a` above `a*`) each match paths the other does not, and neither is dead.
 */
export function shadowedRules(rules: readonly ExclusionRule[]): { pattern: string; shadowedBy: string }[] {
  const compiled = rules.map((r) => ({
    pattern: r.pattern,
    c: r && typeof r.pattern === 'string' && isValidFolderRetention(r.versions)
      ? compileOne(r.pattern, r.versions)
      : null,
  }))
  const out: { pattern: string; shadowedBy: string }[] = []
  for (let j = 0; j < compiled.length; j++) {
    const self = compiled[j]!
    if (!self.c) continue
    // What this rule NAMES, as segments — without the synthetic `**` a name
    // rule carries for matching, or every name rule would be asking whether
    // the earlier one covers a path beginning with a literal `**`.
    const named = self.c.named
    for (let i = 0; i < j; i++) {
      const prior = compiled[i]!
      if (!prior.c) continue
      if (coversPath(prior.c.segs, named)) {
        out.push({ pattern: self.pattern, shadowedBy: prior.pattern })
        break
      }
    }
  }
  return out
}

/**
 * A compiled set of folder rules.
 *
 * Compile ONCE PER SYNC RUN and pass it down. Compiling per entry would put
 * the parsing on the hot path.
 *
 * `retentionFor` runs once per file write, so it can afford to walk the path's
 * ancestor chain. Check `hasRetentionRules` first: for a tenant with no rules
 * at all the write path can skip resolving the file's path entirely, which is
 * a database round trip it would otherwise pay per write.
 */
export interface ExclusionMatcher {
  /**
   * The version cap the first matching rule puts on this file, or `undefined`
   * when no rule covers it (the file then falls through to the rest of the
   * #590 ladder: per-file, per-extension, per-kind, tenant, platform,
   * shipped).
   *
   * `relPath` is the file's tree-relative path, matched WHOLE — the last
   * segment is the file name and a pattern may name it (`docs/*.txt`), which
   * is the difference between this and the directories-only matcher it
   * replaced.
   *
   * ── THE FIRST MATCH IN LIST ORDER WINS ──────────────────────────────────
   *
   * Not the deepest, and not the most specific. The list is ORDERED, the user
   * drags it into the order they want, and the rule nearest the top decides.
   *
   * It resolved by depth once, so `Videos/Kids` beat `Videos` wherever both
   * matched, and order was only a tiebreaker between rules at the same depth.
   * That is a defensible resolution on its own and it was changed for the
   * screen, not the engine: a settings page that lets you drag rows but
   * silently overrides your ordering on most of them is worse than one with
   * no drag at all — the control does nothing and never says so.
   *
   * The cost is that a general rule placed above a specific one kills it.
   * `shadowedRules` finds exactly that case so the screen can say so, which
   * is the trade: ordering that always means something, plus a warning where
   * it means something surprising.
   */
  retentionFor(relPath: string): number | undefined
  /** The first matching rule itself (its normalised pattern and cap) — `retentionFor` is its `versions`. The retention
   *  preview names it (#1442), so the two can never disagree about which rule applies. */
  ruleFor(relPath: string): { pattern: string; versions: number } | undefined
  /** True when at least one rule exists. When false, the write path must not
   *  spend a query resolving a file's path to ask. */
  readonly hasRetentionRules: boolean
  /**
   * The SMALLEST cap any rule here can impose, or `undefined` with no rules.
   *
   * This exists to keep a database round trip off the hottest write path.
   * `cleanupOldVersions` needs a file's drive-relative path before it can ask
   * `retentionFor`, and resolving that path is a recursive CTE — per file
   * write, for every tenant, because the seeded rules mean everyone has rules.
   *
   * The caller does not need the path to rule the whole layer OUT, though. The
   * final cap is either some rule's value or the answer from the layers below,
   * so the lowest cap ANY resolution could produce is the minimum of this and
   * that. A file with no more versions than that floor cannot be pruned under
   * any resolution, so the CTE is skipped and the folder layer never asked.
   *
   * That covers the case the cost would otherwise fall hardest on: a fresh
   * clone or install, where every one of ~110,000 files has exactly one
   * version.
   */
  readonly minRetention: number | undefined
}

const EMPTY_MATCHER: ExclusionMatcher = {
  retentionFor: () => undefined,
  ruleFor: () => undefined,
  hasRetentionRules: false,
  minRetention: undefined,
}

/**
 * Compile rules into a matcher, dropping any that `normalizePattern` or
 * `isValidFolderRetention` refuses.
 *
 * Silent dropping is correct HERE and nowhere else: this is the read path, and
 * a stored rule that has since become invalid must not take the sync down. The
 * WRITE path (`validateExclusionRules`) refuses and names every offender,
 * which is where a user finds out. `compileExclusions` is deliberately total.
 */
export function compileExclusions(rules: Iterable<ExclusionRule>): ExclusionMatcher {
  const valued: CompiledPattern[] = []
  let count = 0
  for (const raw of rules) {
    if (count >= MAX_PATTERNS) break
    if (!raw || typeof raw.pattern !== 'string') continue
    if (!isValidFolderRetention(raw.versions)) continue
    const c = compileOne(raw.pattern, raw.versions)
    if (!c) continue
    count++
    valued.push(c)
  }
  if (valued.length === 0) return EMPTY_MATCHER

  let minRetention = valued[0]!.versions
  for (const r of valued) if (r.versions < minRetention) minRetention = r.versions

  const ruleFor = (relPath: string): { pattern: string; versions: number } | undefined => {
      if (relPath === '') return undefined
      // The WHOLE path, file name included. The file name used to be sliced
      // off here, which is what made `docs/*.txt` and `*.log` match nothing at
      // all while looking perfectly healthy on the settings screen.
      const segs = relPath.split('/')
      for (const r of valued) {
        if (coversPath(r.segs, segs)) return { pattern: r.pattern, versions: r.versions }
      }
      return undefined
  }
  return {
    hasRetentionRules: true,
    minRetention,
    retentionFor: (relPath: string): number | undefined => ruleFor(relPath)?.versions,
    ruleFor,
  }
}
